import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import { useModals } from "ModalProvider";
import { OdooContext } from "OdooProvider";
import { useContext, useState } from "react";
import { addressKeyMap, customerKeyMap } from "./config";
import CreateOdooSOView from "./CreateOdooSOView";
import CustomerView from "./CustomerView";
import { FETCH_ODOO_EXTERNAL_SALES_ORDERS } from "./graphql";

const normalizeProductCode = (code) => String(code ?? "").replace(/[-_]/g, "");
const normalizeSearchText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .trim();

const normalizeExternalId = (value) => String(value ?? "").trim();
const normalizeProductExternalId = (value) => String(value ?? "").trim();

const OrdersView = ({
  source,
  externalOrders,
  incOdooProducts,
  odooCustomers,
  q,
}) => {
  const { odooUrlInc } = useContext(OdooContext);

  const externalOrderIds = externalOrders.map((order) => order.sourceOrderId);

  const { loading, error, data } = useQuery(FETCH_ODOO_EXTERNAL_SALES_ORDERS, {
    variables: { sourceType: source, sourceIds: externalOrderIds },
  });

  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const modal = useModals();

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const odooSaleOrders = JSON.parse(data?.odooSaleOrders).records || [];

  const odooProductsByCode = new Map(
    incOdooProducts
      .filter((product) => product.default_code)
      .map((product) => [normalizeProductCode(product.default_code), product]),
  );
  const odooProductsByMarkettimeId = new Map(
    incOdooProducts
      .filter((product) => product.x_studio_markettime_product_id != null)
      .map((product) => [
        normalizeProductExternalId(product.x_studio_markettime_product_id),
        product,
      ]),
  );
  const customerKey = customerKeyMap[source];
  const addressKey = addressKeyMap[source];

  const normalizedQ = normalizeSearchText(q);
  const filteredExternalOrders = normalizedQ
    ? externalOrders.filter((order) => {
        const matchesOrderNumber = normalizeSearchText(
          order.orderNumber,
        ).includes(normalizedQ);
        const matchesContactName = normalizeSearchText(
          order.contact?.name,
        ).includes(normalizedQ);
        const matchesSku = order.lines?.some((line) =>
          [line.buyerSku, line.vendorSku].some((sku) =>
            normalizeSearchText(sku).includes(normalizedQ),
          ),
        );

        return matchesOrderNumber || matchesContactName || matchesSku;
      })
    : externalOrders;

  const orders = filteredExternalOrders.map((order) => {
    const lines = order.lines.map((line) => ({
      ...line,
      odooProduct:
        source === "markettime"
          ? odooProductsByMarkettimeId.get(
              normalizeProductExternalId(line.productExternalId),
            )
          : odooProductsByCode.get(normalizeProductCode(line.buyerSku)),
    }));
    const productsVerified = lines.every((line) => !!line.odooProduct);
    const externalOdooCustomers = customerKey
      ? odooCustomers.filter(
          (customer) =>
            normalizeExternalId(customer[customerKey]) ===
            normalizeExternalId(order.contact?.externalId),
        )
      : [];
    const odooCustomer =
      externalOdooCustomers.length === 1 ? externalOdooCustomers[0] : null;
    const odooDeliveryAddress =
      odooCustomer && addressKey
        ? odooCustomer.child_ids?.find(
            (address) =>
              address.type === "delivery" &&
              normalizeExternalId(address[addressKey]) ===
                normalizeExternalId(order.shipTo?.externalId),
          )
        : null;

    const odooSaleOrder = odooSaleOrders.find(
      (so) => so.x_studio_source_id === order.sourceOrderId,
    );

    return {
      ...order,
      id: `${source}-${order.orderNumber}`,
      externalOdooCustomers,
      odooCustomer,
      odooDeliveryAddress,
      lines,
      verified: productsVerified && !!odooCustomer && !!odooDeliveryAddress,
      odooSaleOrder,
    };
  });

  const verifiedOrders = orders.filter((order) => order.verified);

  function sendToOdoo() {
    const selectedOrders = orders.filter((order) =>
      selectedOrderIds.includes(order.id),
    );
    modal.present({
      title: "Create Sales Orders in Odoo",
      maxWidth: "max-w-6xl",
      children: (
        <CreateOdooSOView
          source={source}
          orders={selectedOrders}
          hide={modal.hide}
          onCompleted={() => {
            modal.hide();
            setSelectedOrderIds([]);
          }}
        />
      ),
    });
  }

  return (
    <div className="flex flex-col h-full overflow-auto">
      <div className="flex-1 overflow-auto">
        <table className="font-code ">
          <thead>
            <tr className="whitespace-nowrap sticky top-0 z-10 bg-gray-50 dark:bg-gray-700">
              <th className="w-4 py-3 px-4">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedOrderIds(
                        verifiedOrders.map((order) => order.id),
                      );
                    } else {
                      setSelectedOrderIds([]);
                    }
                  }}
                  disabled={verifiedOrders.length === 0}
                  checked={
                    verifiedOrders.length > 0 &&
                    selectedOrderIds.length === verifiedOrders.length
                  }
                />
              </th>
              {selectedOrderIds.length > 0 ? (
                <th colSpan={8} className="font-sans">
                  <div className="flex items-center space-x-4">
                    <div>{selectedOrderIds.length} Selected Orders</div>
                    <Button onClick={sendToOdoo}>Send to Odoo</Button>
                    <Button onClick={() => setSelectedOrderIds([])} danger>
                      Clear Selection
                    </Button>
                  </div>
                </th>
              ) : (
                <>
                  <th>Order Number</th>
                  <th>Order Date</th>
                  <th>Customer</th>
                  <th>Products</th>
                  <th>Payment Terms</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Status</th>
                  <th className="text-right px-4">Odoo</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {orders.map((order, index) => (
              <tr
                key={index}
                className={`border-y border-gray-100 dark:border-gray-700
                  ${!order.verified && "bg-gray-200/80 dark:bg-gray-900/80"}
                  `}
              >
                <td className="px-4">
                  <input
                    type="checkbox"
                    disabled={!order.verified}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedOrderIds([...selectedOrderIds, order.id]);
                      } else {
                        setSelectedOrderIds(
                          selectedOrderIds.filter((id) => id !== order.id),
                        );
                      }
                    }}
                    checked={selectedOrderIds.includes(order.id)}
                  />
                </td>
                <td>{order.orderNumber}</td>
                <td className="whitespace-nowrap">{order.orderDate}</td>
                <td className="font-sans">
                  {order.contact ? (
                    <CustomerView order={order} source={source} />
                  ) : (
                    <div className="text-red-500">
                      No customer info (please check the source system for
                      details)
                    </div>
                  )}
                </td>
                <td className="whitespace-normal">
                  <div className="flex flex-wrap">
                    {order.lines.map((i, index) => (
                      <span
                        key={index}
                        className={`mr-1
                            ${!i.odooProduct && "text-red-500"}`}
                      >
                        {i.buyerSku}({i.quantity})
                      </span>
                    ))}
                  </div>
                </td>
                <td>{order.paymentTerms || "-"}</td>
                <td className="text-right">{order.total.toFixed(2)}</td>
                <td className="text-right">{order.status}</td>
                <td className="text-right whitespace-nowrap px-4">
                  {order.odooSaleOrder ? (
                    <a
                      className="text-purple-600"
                      href={`${odooUrlInc}/odoo/sales/${order.odooSaleOrder.id}`}
                      target="_blank"
                    >
                      {order.odooSaleOrder.name}
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-2 opacity-70 px-4 py-2 bg-gray-50 dark:bg-gray-700">
        🔴 Text in red means that the data is not connected correctly with Odoo.
        Connect them before send to Odoo. If you have any questions, contact
        Charlie for assistance.
      </div>
    </div>
  );
};

export default OrdersView;
